import { useState, useEffect } from 'react';
import { useContract } from './useContract';
import { gameDB } from '../services/db';
import { GAME_CONFIG } from '../config/game';
import toast from 'react-hot-toast';

interface AutoClicker {
  type: number;
  owned: number;
  maxOwned: number;
}

export function useGamePurchases(walletAddress: string | null) {
  const { isDemoMode } = useContract();
  const [ownedBonuses, setOwnedBonuses] = useState<{[key: string]: number}>({});
  const [ownedMultipliers, setOwnedMultipliers] = useState<{[key: string]: number}>({});
  const [autoClickers, setAutoClickers] = useState(
    GAME_CONFIG.autoClickers.map(ac => ({
      ...ac,
      owned: 0,
      price: ac.basePrice
    }))
  );

  // Load data
  useEffect(() => {
    async function loadPurchases() {
      if (!walletAddress) return;
      try {
        const savedPurchases = await gameDB.loadPurchases(walletAddress);
        if (savedPurchases) {
          setOwnedBonuses(savedPurchases.bonuses || {});
          setOwnedMultipliers(savedPurchases.multipliers || {});
          setAutoClickers(prev => prev.map(ac => ({
            ...ac,
            owned: savedPurchases.autoClickers?.find((sac: AutoClicker) => sac.type === ac.type)?.owned || 0,
            price: ac.basePrice * Math.pow(GAME_CONFIG.priceIncreaseFactor, ac.owned || 0)
          })));
        }
      } catch (error) {
        console.error('Error loading purchases:', error);
      }
    }
    loadPurchases();
  }, [walletAddress]);

  // Save data
  const savePurchases = async () => {
    if (!walletAddress) return;
    try {
      await gameDB.savePurchases(walletAddress, {
        bonuses: ownedBonuses,
        multipliers: ownedMultipliers,
        autoClickers: autoClickers.map(ac => ({
          type: ac.type,
          owned: ac.owned,
          maxOwned: ac.maxOwned
        }))
      });
    } catch (error) {
      console.error('Error saving purchases:', error);
    }
  };

  // Calculate current price
  const calculatePrice = (basePrice: number, owned: number): number => {
    return Math.floor(basePrice * Math.pow(GAME_CONFIG.priceIncreaseFactor, owned));
  };

  // Buy additive bonus
  const buyBonus = (bonusId: string, balance: number): number | null => {
    const bonus = GAME_CONFIG.additiveBonus.find(b => b.id === bonusId);
    if (!bonus) return null;

    const owned = ownedBonuses[bonusId] || 0;
    if (owned >= bonus.maxOwned) {
      toast.error('Maximum limit reached for this bonus');
      return null;
    }

    const currentPrice = calculatePrice(bonus.basePrice, owned);
    if (balance < currentPrice) {
      toast.error('Not enough coins!');
      return null;
    }

    setOwnedBonuses(prev => ({
      ...prev,
      [bonusId]: (prev[bonusId] || 0) + 1
    }));

    savePurchases();
    return currentPrice;
  };

  // Buy auto-clicker
  const buyAutoClicker = (type: number, balance: number): number | null => {
    const autoClicker = autoClickers.find(ac => ac.type === type);
    if (!autoClicker) return null;

    if (autoClicker.owned >= autoClicker.maxOwned) {
      toast.error('Maximum limit reached for this auto-clicker');
      return null;
    }

    if (balance < autoClicker.price) {
      toast.error('Not enough coins!');
      return null;
    }

    const cost = autoClicker.price;
    setAutoClickers(prev => prev.map(ac => 
      ac.type === type 
        ? {
            ...ac,
            owned: ac.owned + 1,
            price: calculatePrice(ac.basePrice, ac.owned + 1)
          }
        : ac
    ));

    savePurchases();
    return cost;
  };

  // Calculate total additive bonus
  const calculateTotalBonus = (): number => {
    return GAME_CONFIG.additiveBonus.reduce((total, bonus) => {
      const owned = ownedBonuses[bonus.id] || 0;
      return total + (bonus.value * owned);
    }, 0);
  };

  // Calculate total multiplier
  const calculateMultiplier = (): number => {
    let totalMultiplier = 1;
    Object.entries(ownedMultipliers).forEach(([id, owned]) => {
      const multiplier = GAME_CONFIG.multipliers.find(m => `multiplier_${m.type}` === id);
      if (multiplier && owned > 0) {
        totalMultiplier *= Math.pow(multiplier.type, owned);
      }
    });
    return totalMultiplier;
  };

  return {
    ownedBonuses,
    ownedMultipliers,
    autoClickers,
    buyBonus,
    buyAutoClicker,
    calculateTotalBonus,
    calculateMultiplier
  };
}